import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { TrendingDown, Store, DollarSign, EyeOff, Users, Clock, Target, RefreshCw, Layers, TrendingUp, Briefcase, UserCog } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const BUSINESS_CHALLENGES = [
  {
    icon: Store,
    title: "Espansione frenata dalla mancanza di Store Manager",
    desc: "Un piano di nuove aperture attivo, ma senza figure manageriali qualificate, pronte e allineate alla cultura aziendale, ogni nuovo punto vendita rischia di sottoperformare.",
  },
  {
    icon: TrendingDown,
    title: "Performance degli store e ritorno sugli investimenti",
    desc: "Ogni nuova apertura è un investimento significativo: senza lo Store Manager giusto a presidiarla, quel punto vendita non esprime il suo potenziale. Perdere persone di valore non intercettate in tempo significa compromettere la redditività.",
  },
  {
    icon: DollarSign,
    title: "Dipendenza dal mercato esterno",
    desc: "Rincorrere il fabbisogno con selezione esterna significa costi più alti, tempi di inserimento più lunghi e un rischio concreto di mismatch culturale, in un settore dove trovare profili formati è sempre più difficile.",
  },
];

const HR_CHALLENGES = [
  {
    icon: EyeOff,
    title: "Invisibilità del talento in rete",
    desc: "Nessuna visibilità oggettiva sulle competenze di migliaia di addetti vendita distribuiti su oltre 570 punti vendita: le decisioni sul potenziale si basavano su numeri di vendita e percezioni soggettive.",
  },
  {
    icon: Users,
    title: "Valutazioni frammentate e non confrontabili",
    desc: "Ogni manager di negozio valutava con criteri propri, ogni area funzionava con logiche diverse: due persone con lo stesso potenziale potevano avere percorsi completamente diversi, non per merito ma per fortuna geografica.",
  },
  {
    icon: Clock,
    title: "Gestione reattiva del talento, non predittiva",
    desc: "Senza visibilità strutturata sul potenziale interno, ogni posizione chiave da coprire si trasformava in un processo di selezione esterna. Un doppio costo: tempi e investimenti per reclutare, più il rischio di perdere talenti già in azienda non valorizzati.",
  },
];

const OBJECTIVES = [
  {
    icon: Target,
    text: "Ottenere una lettura comune e oggettiva del potenziale su tutta la rete: stessi criteri, stesso linguaggio, indipendentemente dal territorio o dal singolo responsabile",
  },
  {
    icon: RefreshCw,
    text: "Costruire una pipeline interna di Store Manager pronti, per sostenere il piano di aperture senza dipendere da un mercato esterno sempre più competitivo e costoso",
  },
  {
    icon: Layers,
    text: "Massimizzare la performance dei punti vendita e il ritorno sugli investimenti in nuove aperture, garantendo che ogni store sia presidiato da uno Store Manager qualificato e allineato alla cultura aziendale",
  },
  {
    icon: TrendingUp,
    text: "Anticipare il fabbisogno di ruoli chiave con mesi di anticipo, passando da una logica emergenziale a una capacità predittiva di pianificazione del talento",
  },
];

export default function ChallengeSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="sfida"
      data-testid="challenge-section"
      className="py-20 md:py-28 bg-[var(--sv-surface)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <Badge
            data-testid="challenge-badge"
            className="mb-4 bg-red-50 text-red-600 border-red-200 hover:bg-red-50 font-medium text-xs tracking-wide uppercase px-3 py-1"
          >
            La Sfida
          </Badge>
          <h2
            data-testid="challenge-title"
            className="font-['Inter_Tight'] text-3xl md:text-4xl font-bold tracking-tight text-[var(--sv-secondary)]"
          >
            Il problema strutturale
          </h2>
          <p className="mt-3 text-base text-slate-500 max-w-2xl">
            In's Mercato è un'azienda in forte crescita. Con oltre 570 punti vendita e un piano di sviluppo immobiliare attivo, il business aveva un vincolo operativo che precedeva qualsiasi considerazione HR.
          </p>
        </motion.div>

        {/* BUSINESS Challenges */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="w-5 h-5 text-red-500" />
            <h3 className="font-['Inter_Tight'] font-bold text-lg text-[var(--sv-secondary)]">Business</h3>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {BUSINESS_CHALLENGES.map((challenge, i) => (
              <motion.div
                key={challenge.title}
                initial={{ opacity: 0, y: 16 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
              >
                <Card
                  data-testid={`business-challenge-${i}`}
                  className="p-5 h-full border-red-100 bg-white bento-card"
                >
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center mb-4">
                    <challenge.icon className="w-5 h-5 text-red-500" />
                  </div>
                  <h3 className="font-['Inter_Tight'] font-semibold text-[var(--sv-secondary)] text-sm mb-2">
                    {challenge.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {challenge.desc}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* HR Challenges */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mb-16"
        >
          <div className="flex items-center gap-2 mb-4">
            <UserCog className="w-5 h-5 text-amber-600" />
            <h3 className="font-['Inter_Tight'] font-bold text-lg text-[var(--sv-secondary)]">HR</h3>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {HR_CHALLENGES.map((challenge, i) => (
              <motion.div
                key={challenge.title}
                initial={{ opacity: 0, y: 16 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.35 + i * 0.08 }}
              >
                <Card
                  data-testid={`hr-challenge-${i}`}
                  className="p-5 h-full border-amber-100 bg-white bento-card"
                >
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-4">
                    <challenge.icon className="w-5 h-5 text-amber-600" />
                  </div>
                  <h3 className="font-['Inter_Tight'] font-semibold text-[var(--sv-secondary)] text-sm mb-2">
                    {challenge.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {challenge.desc}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <Separator className="mb-16" />

        {/* Business objectives */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Badge
            data-testid="objectives-badge"
            className="mb-4 bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50 font-medium text-xs tracking-wide uppercase px-3 py-1"
          >
            Obiettivi di Collaborazione
          </Badge>
          <h2
            data-testid="objectives-title"
            className="font-['Inter_Tight'] text-2xl md:text-3xl font-bold tracking-tight text-[var(--sv-secondary)] mb-8"
          >
            Cosa doveva cambiare
          </h2>

          <div className="grid sm:grid-cols-2 gap-4">
            {OBJECTIVES.map((obj, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.5 + i * 0.1 }}
                data-testid={`objective-card-${i}`}
                className="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-100"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <obj.icon className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed pt-1.5">
                  {obj.text}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
